import { actions, MessagesStore, MESSAGES_STORE_NAME } from './Store';
import { frontendActions, getConfig, LocationType } from '@cmctechnology/webinvest-store-frontend';
import { CmcWebApi, CmcWebApiCommon, webTradingApi } from '@cmctechnology/webinvest-api-client-web-trading';
import { Dispatch } from '@reduxjs/toolkit';

export const MESSAGE_API_NAMES = {
  UnreadMessageCount1: `UnreadMessageCount1`,
  PlatformMessageGetLatest1: `PlatformMessageGetLatest1`,
  PlatformMessageMarkAsRead1: `PlatformMessageMarkAsRead1`
};

type GetState = typeof MessagesStore.getState;

// eslint-disable-next-line unicorn/consistent-function-scoping
export const fetchUnreadMessageCount = () => async (dispatch: Dispatch<any>, getState: GetState) => {
  // hash is required for polling web api's to allow for cached responses
  const hash = getState()[MESSAGES_STORE_NAME].unreadMessages.hash;
  const request = { _config: { hash } };

  const callWebApi = async () => {
    return webTradingApi(getConfig().stockbrokingApiUrl).send<CmcWebApi.Shared.RecordCountModel1>(
      {
        [MESSAGE_API_NAMES.UnreadMessageCount1]: request
      },
      false,
      true
    );
  };

  try {
    const response = await frontendActions.apiCall(MESSAGES_STORE_NAME, MESSAGE_API_NAMES.UnreadMessageCount1, callWebApi)(dispatch);
    dispatch(actions.unreadMessagesUpdated({ count: response?.RecordCount, hash: response?._apiInfo?.Hash ?? 0 }));
  } catch {}
};

// eslint-disable-next-line unicorn/consistent-function-scoping
export const onPollUnreadMessageCount = () => (dispatch: Dispatch<any>) => {
  dispatch(fetchUnreadMessageCount());
};

const UNREAD_MESSAGE_COUNT_POLL_TIMEOUT = 5_000;

export const subscribeUnreadMessageCount = (location: LocationType) => (dispatch: Dispatch<any>) => {
  dispatch(
    frontendActions.subscribe({
      onPoll: onPollUnreadMessageCount,
      location,
      apiName: MESSAGE_API_NAMES.UnreadMessageCount1,
      storeName: MESSAGES_STORE_NAME,
      pollTimeout: UNREAD_MESSAGE_COUNT_POLL_TIMEOUT
    })
  );
};

export const unsubscribeUnreadMessageCount = (location: LocationType) => (dispatch: Dispatch<any>) => {
  dispatch(
    frontendActions.unsubscribe({
      location,
      apiName: MESSAGE_API_NAMES.UnreadMessageCount1,
      storeName: MESSAGES_STORE_NAME
    })
  );
};

// eslint-disable-next-line unicorn/consistent-function-scoping
export const fetchPlatformMessages = (displayUrl?: string) => async (dispatch: Dispatch<any>) => {
  // eslint-disable-next-line unicorn/no-null
  const request = { Id: displayUrl || null };

  try {
    const response = await frontendActions.webTradingApiCall<CmcWebApiCommon.Shared.RawJsonModel1>(
      MESSAGES_STORE_NAME,
      MESSAGE_API_NAMES.PlatformMessageGetLatest1,
      request,
      dispatch,
      undefined,
      true
    );
    dispatch(actions.unreadPlatformMessagesUpdated({ platformMessages: response.Data as any }));
  } catch {}
};

// eslint-disable-next-line unicorn/consistent-function-scoping
export const onPollPlatformMessages = (displayUrl?: string) => (dispatch: Dispatch<any>) => {
  dispatch(fetchPlatformMessages(displayUrl));
};

const PLATFORM_MESSAGE_POLL_TIMEOUT = 60_000;

export const subscribePlatformMessages = (location: LocationType, displayUrl?: string) => (dispatch: Dispatch<any>, getState: GetState) => {
  if (!getState()[MESSAGES_STORE_NAME].platformMessages) {
    dispatch(onPollPlatformMessages(displayUrl));
  }

  dispatch(
    frontendActions.subscribe({
      onPoll: () => onPollPlatformMessages(displayUrl),
      location,
      apiName: MESSAGE_API_NAMES.PlatformMessageGetLatest1,
      storeName: MESSAGES_STORE_NAME,
      pollTimeout: PLATFORM_MESSAGE_POLL_TIMEOUT
    })
  );
};

export const unsubscribePlatformMessages = (location: LocationType) => (dispatch: Dispatch<any>) => {
  dispatch(
    frontendActions.unsubscribe({
      location,
      apiName: MESSAGE_API_NAMES.PlatformMessageGetLatest1,
      storeName: MESSAGES_STORE_NAME
    })
  );
};

export const platformMessageMarkAsRead = (messageId: string) => async (dispatch: Dispatch<any>) => {
  const request = { Id: messageId };

  try {
    await frontendActions.webTradingApiCall(MESSAGES_STORE_NAME, MESSAGE_API_NAMES.PlatformMessageMarkAsRead1, request, dispatch);
  } catch {}
};
